import {
  waterfallPreferredDividendsAquisitionInvestorsMolecule,
  waterfallPreferredDividendsSearchPhaseInvestorsMolecule,
  waterfallPreferredDividendsSearchersCoinvestMolecule,
  waterfallPreferredDividendsSellerRolloverMolecule,
  waterfallPrincipalRepaymentAquisitionInvestorsMolecule,
  waterfallPrincipalRepaymentCumulativePrincipalRepaidMolecule,
  waterfallPrincipalRepaymentSearchPhaseInvestorsMolecule,
  waterfallPrincipalRepaymentSearchersCoinvestMolecule,
  waterfallPrincipalRepaymentSellerRolloverMolecule,
  waterfallProfitDistributionsAcquisitionInvestorsMolecule,
  waterfallProfitDistributionsPromoteSearchersCarryMolecule,
  waterfallProfitDistributionsSearchPhaseInvestorsMolecule,
  waterfallProfitDistributionsSearchersCoinvestMolecule,
  waterfallProfitDistributionsSellerRolloverMolecule,
  waterfallTaxDistributionsAcquisitionInvestorsMolecule,
  waterfallTaxDistributionsPromoteSearchersCarryMolecule,
  waterfallTaxDistributionsSearchPhaseInvestorsMolecule,
  waterfallTaxDistributionsSearchersCoinvestMolecule,
  waterfallTaxDistributionsSellerRolloverMolecule,
  waterfallcompanySaleAcquisitionInvestorsMolecule,
  waterfallcompanySalePromoteSearchersCarryMolecule,
  waterfallcompanySaleSearchPhaseInvestorsMolecule,
  waterfallcompanySaleSearchersCoinvestMolecule,
  waterfallcompanySaleSellerRolloverMolecule,
} from "@/financeModels/owasco/projectionsWaterfallTables";
import { toNumberOrNull } from "@/utils/math";
import { irr } from "@travishorn/finance";
import { Atom, Getter, atom } from "jotai";
import { MathEvaluteError, MathResult } from "../types";
import {
  acquisitionInvestorsCapitalInvestedAtom,
  acquisitionInvestorsPercentCommonEquityAtom,
  acquisitionInvestorsPercentPreferredEquityAtom,
  coInvestorsPercentPreferredEquityAtom,
  promoteSearchersCarryPercentCommonEquityAtom,
  searchPhaseInvestorsCapitalInvestedAtom,
  searchPhaseInvestorsPercentCommonEquityAtom,
  searchPhaseInvestorsPercentPreferredEquityAtom,
  searcherCoInvestPercentCommonEquityAtom,
  sellerRolloverCapitalInvestedAtom,
  sellerRolloverPercentCommonEquityAtom,
  sellerRolloverPercentPreferredEquityAtom,
  totalCapitalInvestedAtom,
} from "./companyOwnership";
import {
  businessEntityTypeAtom,
  equityDollarAtom,
  equityDollarWithCapitalAtom,
  equityInvestorEquityStepUpAtom,
  equitySearchPhaseCapitalDollarAtom,
  equitySellerRolloverDollarAtom,
  equityTreatmentAcquisitionInvestorsApplyStepUpAtom,
  equityTreatmentAcquisitionInvestorsAtom,
  equityTreatmentSearchPhaseApplyStepUpAtom,
  equityTreatmentSearchPhaseAtom,
  equityTreatmentSearchersCoInvestApplyStepUpAtom,
  equityTreatmentSearchersCoInvestAtom,
  equityTreatmentSellerEquityRolloverApplyStepUpAtom,
  equityTreatmentSellerEquityRolloverAtom,
  includeTaxDistributionsInIRRAndMOICAtom,
  individualInvestorReturnStartDollarAtom,
  isEquityInvestorsIncludedAtom,
  isInvestorFundedSearchPhaseAtom,
  isSellerRollingOverEquityAtom,
  sbaLoanStartDateAtom,
} from "./general";
import { createComputedIterationMolecule } from "./iterations";
import { CellTypes } from "./types";

const sumMolecule = (molecule: Atom<Atom<CellTypes>[]>) => {
  return atom((get) => {
    const values = get(molecule).map((a) => Number(get(a).value) || 0);
    return Number(
      values.reduce((sum, value, i) => {
        if (i < 2) return sum || 0; // Skip first two iterations
        return Number(sum || 0) + value;
      }, 0),
    );
  });
};

export const principalRepaymentSearchPhaseInvestorsSumAtom = sumMolecule(
  waterfallPrincipalRepaymentSearchPhaseInvestorsMolecule,
);

export const principalRepaymentAquisitionInvestorsSumAtom = sumMolecule(
  waterfallPrincipalRepaymentAquisitionInvestorsMolecule,
);

export const principalRepaymentSearchersCoinvestSumAtom = sumMolecule(
  waterfallPrincipalRepaymentSearchersCoinvestMolecule,
);

export const principalRepaymentSellerRolloverSumAtom = sumMolecule(
  waterfallPrincipalRepaymentSellerRolloverMolecule,
);

export const preferredDividendsSearchPhaseInvestorsSumAtom = sumMolecule(
  waterfallPreferredDividendsSearchPhaseInvestorsMolecule,
);

export const preferredDividendsAquisitionInvestorsSumAtom = sumMolecule(
  waterfallPreferredDividendsAquisitionInvestorsMolecule,
);
export const preferredDividendsSearchersCoinvestSumAtom = sumMolecule(
  waterfallPreferredDividendsSearchersCoinvestMolecule,
);

export const preferredDividendsSellerRolloverSumAtom = sumMolecule(
  waterfallPreferredDividendsSellerRolloverMolecule,
);

export const profitDistributionsSearchPhaseInvestorsSumAtom = sumMolecule(
  waterfallProfitDistributionsSearchPhaseInvestorsMolecule,
);

export const profitDistributionsAcquisitionInvestorsSumAtom = sumMolecule(
  waterfallProfitDistributionsAcquisitionInvestorsMolecule,
);

export const profitDistributionsSearchersCoinvestSumAtom = sumMolecule(
  waterfallProfitDistributionsSearchersCoinvestMolecule,
);

export const profitDistributionsSellerRolloverSumAtom = sumMolecule(
  waterfallProfitDistributionsSellerRolloverMolecule,
);

export const profitDistributionsPromoteSearchersCarrySumAtom = sumMolecule(
  waterfallProfitDistributionsPromoteSearchersCarryMolecule,
);

// now do tax distributions sums

export const taxDistributionsSearchPhaseInvestorsSumAtom = sumMolecule(
  waterfallTaxDistributionsSearchPhaseInvestorsMolecule,
);

export const taxDistributionsAcquisitionInvestorsSumAtom = sumMolecule(
  waterfallTaxDistributionsAcquisitionInvestorsMolecule,
);

export const taxDistributionsSearchersCoinvestSumAtom = sumMolecule(
  waterfallTaxDistributionsSearchersCoinvestMolecule,
);

export const taxDistributionsSellerRolloverSumAtom = sumMolecule(
  waterfallTaxDistributionsSellerRolloverMolecule,
);

function createPreferredTotalDistributionsAtom(
  preferredDividendsSumMolecule: Atom<number>,
  principalRepaymentsSumMolecule: Atom<number>,
) {
  return atom((get) => {
    const preferredDividends = get(preferredDividendsSumMolecule);
    const principalRepayments = get(principalRepaymentsSumMolecule);
    return preferredDividends + principalRepayments;
  });
}

export const searchPhaseInvestorsPreferedTotalDistributionsAtom =
  createPreferredTotalDistributionsAtom(
    preferredDividendsSearchPhaseInvestorsSumAtom,
    principalRepaymentSearchPhaseInvestorsSumAtom,
  );

export const acquisitionInvestorsPreferedTotalDistributionsAtom =
  createPreferredTotalDistributionsAtom(
    preferredDividendsAquisitionInvestorsSumAtom,
    principalRepaymentAquisitionInvestorsSumAtom,
  );

export const searchersCoinvestPreferedTotalDistributionsAtom =
  createPreferredTotalDistributionsAtom(
    preferredDividendsSearchersCoinvestSumAtom,
    principalRepaymentSearchersCoinvestSumAtom,
  );

export const sellerRolloverPreferedTotalDistributionsAtom =
  createPreferredTotalDistributionsAtom(
    preferredDividendsSellerRolloverSumAtom,
    principalRepaymentSellerRolloverSumAtom,
  );

function createCommonTotalDistributionsAtom(
  profitDistributionsSumMolecule: Atom<number>,
  taxDistributionsSumMolecule: Atom<number>,
) {
  return atom((get) => {
    const isTaxIncluded = get(includeTaxDistributionsInIRRAndMOICAtom);
    const amount = get(profitDistributionsSumMolecule);
    const tax = get(taxDistributionsSumMolecule);

    // TODO: THIS IS INCORRECT, NEED TO COME BACK TO THIS

    if (!isTaxIncluded) {
      return amount;
    }

    return amount + tax;
  });
}

function createROITaxesMolecule(
  taxDistributionsMolecule: Atom<Atom<CellTypes>[]>,
) {
  return createComputedIterationMolecule((i) => {
    return atom((get) => {
      const Assumptions_D158 = get(businessEntityTypeAtom);

      const taxValue = Number(get(get(taxDistributionsMolecule)[i]).value);

      const value = Assumptions_D158 === "ccorp" ? 0 : taxValue;

      return {
        isLocked: true,
        type: "dollar",
        value,
      };
    });
  });
}

export const searchPhaseInvestorsROITaxesMolecule = createROITaxesMolecule(
  waterfallTaxDistributionsSearchPhaseInvestorsMolecule,
);

export const acquisitionInvestorsROITaxesMolecule = createROITaxesMolecule(
  waterfallTaxDistributionsAcquisitionInvestorsMolecule,
);

export const searchersCoinvestROITaxesMolecule = createROITaxesMolecule(
  waterfallTaxDistributionsSearchersCoinvestMolecule,
);

export const sellerRolloverROITaxesMolecule = createROITaxesMolecule(
  waterfallTaxDistributionsSellerRolloverMolecule,
);

export const promoteSearchersCarryROITaxesMolecule = createROITaxesMolecule(
  waterfallTaxDistributionsPromoteSearchersCarryMolecule,
);

export const searchPhaseInvestorsROITaxesSumAtom = sumMolecule(
  searchPhaseInvestorsROITaxesMolecule,
);

export const acquisitionInvestorsROITaxesSumAtom = sumMolecule(
  acquisitionInvestorsROITaxesMolecule,
);

export const searchersCoinvestROITaxesSumAtom = sumMolecule(
  searchersCoinvestROITaxesMolecule,
);

export const sellerRolloverROITaxesSumAtom = sumMolecule(
  sellerRolloverROITaxesMolecule,
);

export const promoteSearchersCarryROITaxesSumAtom = sumMolecule(
  promoteSearchersCarryROITaxesMolecule,
);

export const searchPhaseInvestorsCommonTotalDistributionsAtom =
  createCommonTotalDistributionsAtom(
    profitDistributionsSearchPhaseInvestorsSumAtom,
    searchPhaseInvestorsROITaxesSumAtom,
  );

export const acquisitionInvestorsCommonTotalDistributionsAtom =
  createCommonTotalDistributionsAtom(
    profitDistributionsAcquisitionInvestorsSumAtom,
    acquisitionInvestorsROITaxesSumAtom,
  );

export const searchersCoinvestCommonTotalDistributionsAtom =
  createCommonTotalDistributionsAtom(
    profitDistributionsSearchersCoinvestSumAtom,
    searchersCoinvestROITaxesSumAtom,
  );

export const sellerRolloverCommonTotalDistributionsAtom =
  createCommonTotalDistributionsAtom(
    profitDistributionsSellerRolloverSumAtom,
    sellerRolloverROITaxesSumAtom,
  );

export const promoteSearchersCarryCommonTotalDistributionsAtom =
  createCommonTotalDistributionsAtom(
    profitDistributionsPromoteSearchersCarrySumAtom,
    promoteSearchersCarryROITaxesSumAtom,
  );

function createTotalDistributionsTotalAtom(
  commonTotalDistributionsAtom: Atom<number>,
  preferredTotalDistributionsAtom: Atom<number>,
) {
  return atom((get) => {
    return (
      get(commonTotalDistributionsAtom) + get(preferredTotalDistributionsAtom)
    );
  });
}

export const searchPhaseInvestorsTotalDistributionsTotalAtom =
  createTotalDistributionsTotalAtom(
    searchPhaseInvestorsCommonTotalDistributionsAtom,
    searchPhaseInvestorsPreferedTotalDistributionsAtom,
  );

export const acquisitionInvestorsTotalDistributionsTotalAtom =
  createTotalDistributionsTotalAtom(
    acquisitionInvestorsCommonTotalDistributionsAtom,
    acquisitionInvestorsPreferedTotalDistributionsAtom,
  );

export const searchersCoinvestTotalDistributionsTotalAtom =
  createTotalDistributionsTotalAtom(
    searchersCoinvestCommonTotalDistributionsAtom,
    searchersCoinvestPreferedTotalDistributionsAtom,
  );

export const sellerRolloverTotalDistributionsTotalAtom =
  createTotalDistributionsTotalAtom(
    sellerRolloverCommonTotalDistributionsAtom,
    sellerRolloverPreferedTotalDistributionsAtom,
  );

export const promoteSearchersCarryTotalDistributionsTotalAtom =
  createTotalDistributionsTotalAtom(
    promoteSearchersCarryCommonTotalDistributionsAtom,
    atom(() => 0), // No preferred distributions for promote searchers carry
  );

// sales proceeds sums

export const searchPhaseInvestorsSaleProceedsSumAtom = sumMolecule(
  waterfallcompanySaleSearchPhaseInvestorsMolecule,
);
export const acquisitionInvestorsSaleProceedsSumAtom = sumMolecule(
  waterfallcompanySaleAcquisitionInvestorsMolecule,
);
export const searchersCoinvestSaleProceedsSumAtom = sumMolecule(
  waterfallcompanySaleSearchersCoinvestMolecule,
);
export const sellerRolloverSaleProceedsSumAtom = sumMolecule(
  waterfallcompanySaleSellerRolloverMolecule,
);
export const searchersCarrySaleProceedsSumAtom = sumMolecule(
  waterfallcompanySalePromoteSearchersCarryMolecule,
);

function createTotalReturnAtom(
  preferredTotalDistributionsAtom: Atom<number>,
  saleProceedsSumAtom: Atom<number>,
) {
  return atom((get) => {
    const t = get(preferredTotalDistributionsAtom);
    const s = get(saleProceedsSumAtom);
    return t + s;
  });
}

export const searchPhaseInvestorsPreferredTotalReturnAtom =
  createTotalReturnAtom(
    searchPhaseInvestorsPreferedTotalDistributionsAtom,
    atom(() => 0),
  );

export const searchPhaseInvestorsCommonTotalReturnAtom = createTotalReturnAtom(
  searchPhaseInvestorsCommonTotalDistributionsAtom,
  searchPhaseInvestorsSaleProceedsSumAtom,
);

export const searchPhaseInvestorsTotalReturnAtom = createTotalReturnAtom(
  searchPhaseInvestorsPreferredTotalReturnAtom,
  searchPhaseInvestorsCommonTotalReturnAtom,
);

export const acquisitionInvestorsPreferredTotalReturnAtom =
  createTotalReturnAtom(
    acquisitionInvestorsPreferedTotalDistributionsAtom,
    atom(() => 0),
  );
export const acquisitionInvestorsCommonTotalReturnAtom = createTotalReturnAtom(
  acquisitionInvestorsCommonTotalDistributionsAtom,
  acquisitionInvestorsSaleProceedsSumAtom,
);

export const acquisitionInvestorsTotalReturnAtom = createTotalReturnAtom(
  acquisitionInvestorsPreferredTotalReturnAtom,
  acquisitionInvestorsCommonTotalReturnAtom,
);

export const searchersCoinvestPreferredTotalReturnAtom = createTotalReturnAtom(
  searchersCoinvestPreferedTotalDistributionsAtom,
  atom(() => 0),
);

export const searchersCoinvestCommonTotalReturnAtom = createTotalReturnAtom(
  searchersCoinvestCommonTotalDistributionsAtom,
  searchersCoinvestSaleProceedsSumAtom,
);

export const searchersCoinvestTotalReturnAtom = createTotalReturnAtom(
  searchersCoinvestPreferredTotalReturnAtom,
  searchersCoinvestCommonTotalReturnAtom,
);

export const sellerRolloverPreferredTotalReturnAtom = createTotalReturnAtom(
  sellerRolloverPreferedTotalDistributionsAtom,
  atom(() => 0),
);

export const sellerRolloverCommonTotalReturnAtom = createTotalReturnAtom(
  sellerRolloverCommonTotalDistributionsAtom,
  sellerRolloverSaleProceedsSumAtom,
);

export const sellerRolloverTotalReturnAtom = createTotalReturnAtom(
  sellerRolloverPreferredTotalReturnAtom,
  sellerRolloverCommonTotalReturnAtom,
);

export const promoteSearchersCarryPreferredTotalReturnAtom =
  createTotalReturnAtom(
    atom(() => 0), // No preferred distributions for promote searchers carry
    atom(() => 0),
  );

export const promoteSearchersCarryCommonTotalReturnAtom = createTotalReturnAtom(
  promoteSearchersCarryCommonTotalDistributionsAtom,
  searchersCarrySaleProceedsSumAtom,
);

export const promoteSearchersCarryTotalReturnAtom = createTotalReturnAtom(
  promoteSearchersCarryCommonTotalReturnAtom,
  promoteSearchersCarryPreferredTotalReturnAtom,
);

export const promoteSearchersCarryVisibleAtom = atom((get) => {
  const totalReturn = get(promoteSearchersCarryTotalReturnAtom);
  return totalReturn > 0;
});

// Combined function to generate preferred dividends atoms for each group and their totals
function createInvestorDistributionTableAtoms(
  molecules: Record<string, Atom<Atom<CellTypes>[]>>,
) {
  // Helper to get the preferred dividends values for a molecule
  const createTotalsValueAtom = (molecule: Atom<Atom<CellTypes>[]>) =>
    atom((get) => {
      const arr = get(molecule);

      return arr
        .map((a) => {
          const value = get(a).value;
          return Number(value);
        })
        .slice(2);
    });

  const valueAtoms: Record<string, Atom<number[]>> = {};
  for (const [key, molecule] of Object.entries(molecules)) {
    valueAtoms[key] = createTotalsValueAtom(molecule);
  }

  const totalsValuesAtom = atom((get) => {
    const arrays = Object.values(valueAtoms).map((atomItem) => get(atomItem));
    const length = Math.max(...arrays.map((arr) => arr.length));
    return Array.from({ length }, (_, i) =>
      arrays.reduce((sum, arr) => sum + (arr[i] || 0), 0),
    );
  });

  const totalAtom = atom((get) => {
    const preferredDividends = get(totalsValuesAtom);
    return preferredDividends.reduce((sum, value) => sum + value, 0);
  });

  return {
    valueAtoms,
    totalsValuesAtom,
    totalAtom,
  };
}

const {
  valueAtoms: preferredDividendsValueAtoms,
  totalsValuesAtom: preferredDividendsTotalsValuesAtom,
  totalAtom: preferredDividendsTotalAtom,
} = createInvestorDistributionTableAtoms({
  searchPhase: waterfallPreferredDividendsSearchPhaseInvestorsMolecule,
  acquisition: waterfallPreferredDividendsAquisitionInvestorsMolecule,
  coinvest: waterfallPreferredDividendsSearchersCoinvestMolecule,
  rollover: waterfallPreferredDividendsSellerRolloverMolecule,
});

export const searchPhaseInvestorsPreferredDividendsTotalsValueAtom =
  preferredDividendsValueAtoms.searchPhase;

export const acquisitionInvestorsPreferredDividendsTotalsValueAtom =
  preferredDividendsValueAtoms.acquisition;

export const searchersCoinvestPreferredDividendsTotalsValueAtom =
  preferredDividendsValueAtoms.coinvest;

export const sellerRolloverPreferredDividendsTotalsValueAtom =
  preferredDividendsValueAtoms.rollover;

export { preferredDividendsTotalAtom, preferredDividendsTotalsValuesAtom };

const {
  valueAtoms: principalRepaymentsValueAtoms,
  totalsValuesAtom: principalRepaymentsTotalsValuesAtom,
  totalAtom: principalRepaymentsTotalAtom,
} = createInvestorDistributionTableAtoms({
  searchPhase: waterfallPrincipalRepaymentSearchPhaseInvestorsMolecule,
  acquisition: waterfallPrincipalRepaymentAquisitionInvestorsMolecule,
  coinvest: waterfallPrincipalRepaymentSearchersCoinvestMolecule,
  rollover: waterfallPrincipalRepaymentSellerRolloverMolecule,
});

export const searchPhaseInvestorsPrincipalRepaymentsTotalsValueAtom =
  principalRepaymentsValueAtoms.searchPhase;

export const acquisitionInvestorsPrincipalRepaymentsTotalsValueAtom =
  principalRepaymentsValueAtoms.acquisition;

export const searchersCoinvestPrincipalRepaymentsTotalsValueAtom =
  principalRepaymentsValueAtoms.coinvest;

export const sellerRolloverPrincipalRepaymentsTotalsValueAtom =
  principalRepaymentsValueAtoms.rollover;

export { principalRepaymentsTotalAtom, principalRepaymentsTotalsValuesAtom };

const {
  valueAtoms: profitDistributionsValueAtoms,
  totalsValuesAtom: profitDistributionsTotalsValuesAtom,
  totalAtom: profitDistributionsTotalAtom,
} = createInvestorDistributionTableAtoms({
  searchPhase: waterfallProfitDistributionsSearchPhaseInvestorsMolecule,
  acquisition: waterfallProfitDistributionsAcquisitionInvestorsMolecule,
  coinvest: waterfallProfitDistributionsSearchersCoinvestMolecule,
  rollover: waterfallProfitDistributionsSellerRolloverMolecule,
  //promoteCarry: waterfallProfitDistributionsPromoteSearchersCarryMolecule,
});

export const searchPhaseInvestorsProfitDistributionsTotalsValueAtom =
  profitDistributionsValueAtoms.searchPhase;

export const acquisitionInvestorsProfitDistributionsTotalsValueAtom =
  profitDistributionsValueAtoms.acquisition;

export const searchersCoinvestProfitDistributionsTotalsValueAtom =
  profitDistributionsValueAtoms.coinvest;

export const sellerRolloverProfitDistributionsTotalsValueAtom =
  profitDistributionsValueAtoms.rollover;

export const promoteSearchersCarryProfitDistributionsTotalsValueAtom =
  profitDistributionsValueAtoms.promoteCarry;

export { profitDistributionsTotalAtom, profitDistributionsTotalsValuesAtom };

const {
  valueAtoms: taxDistributionsValueAtoms,
  totalsValuesAtom: taxDistributionsTotalsValuesAtom,
  totalAtom: taxDistributionsTotalAtom,
} = createInvestorDistributionTableAtoms({
  searchPhase: searchPhaseInvestorsROITaxesMolecule,
  acquisition: acquisitionInvestorsROITaxesMolecule,
  coinvest: searchersCoinvestROITaxesMolecule,
  rollover: sellerRolloverROITaxesMolecule,
});

export const searchPhaseInvestorsTaxDistributionsTotalsValueAtom =
  taxDistributionsValueAtoms.searchPhase;

export const acquisitionInvestorsTaxDistributionsTotalsValueAtom =
  taxDistributionsValueAtoms.acquisition;

export const searchersCoinvestTaxDistributionsTotalsValueAtom =
  taxDistributionsValueAtoms.coinvest;

export const sellerRolloverTaxDistributionsTotalsValueAtom =
  taxDistributionsValueAtoms.rollover;

export { taxDistributionsTotalAtom, taxDistributionsTotalsValuesAtom };

const {
  valueAtoms: saleProceedsValueAtoms,
  totalsValuesAtom: saleProceedsTotalsValuesAtom,
  totalAtom: saleProceedsTotalAtom,
} = createInvestorDistributionTableAtoms({
  searchPhase: waterfallcompanySaleSearchPhaseInvestorsMolecule,
  acquisition: waterfallcompanySaleAcquisitionInvestorsMolecule,
  coinvest: waterfallcompanySaleSearchersCoinvestMolecule,
  rollover: waterfallcompanySaleSellerRolloverMolecule,
  //promoteCarry: waterfallcompanySalePromoteSearchersCarryMolecule,
});

export const searchPhaseInvestorsSaleProceedsTotalsValueAtom =
  saleProceedsValueAtoms.searchPhase;
export const acquisitionInvestorsSaleProceedsTotalsValueAtom =
  saleProceedsValueAtoms.acquisition;
export const searchersCoinvestSaleProceedsTotalsValueAtom =
  saleProceedsValueAtoms.coinvest;
export const sellerRolloverSaleProceedsTotalsValueAtom =
  saleProceedsValueAtoms.rollover;
export const promoteSearchersCarrySaleProceedsTotalsValueAtom =
  saleProceedsValueAtoms.promoteCarry;

export { saleProceedsTotalAtom, saleProceedsTotalsValuesAtom };

export const searchPhaseInvestorDistributionsValuesAtom = atom((get) => {
  const includeTax = get(includeTaxDistributionsInIRRAndMOICAtom);

  const preferredDividends = get(
    searchPhaseInvestorsPreferredDividendsTotalsValueAtom,
  );
  const principalRepayments = get(
    searchPhaseInvestorsPrincipalRepaymentsTotalsValueAtom,
  );
  const profitDistributions = get(
    searchPhaseInvestorsProfitDistributionsTotalsValueAtom,
  );
  const taxDistributions = get(
    searchPhaseInvestorsTaxDistributionsTotalsValueAtom,
  );
  const saleProceeds = get(searchPhaseInvestorsSaleProceedsTotalsValueAtom);

  return preferredDividends.map((value, i) => {
    return (
      value +
      principalRepayments[i] +
      profitDistributions[i] +
      (includeTax ? taxDistributions[i] : 0) +
      (saleProceeds[i] || 0)
    );
  });
});

export const acquisitionInvestorDistributionsValuesAtom = atom((get) => {
  const includeTax = get(includeTaxDistributionsInIRRAndMOICAtom);

  const preferredDividends = get(
    acquisitionInvestorsPreferredDividendsTotalsValueAtom,
  );
  const principalRepayments = get(
    acquisitionInvestorsPrincipalRepaymentsTotalsValueAtom,
  );
  const profitDistributions = get(
    acquisitionInvestorsProfitDistributionsTotalsValueAtom,
  );
  const taxDistributions = get(
    acquisitionInvestorsTaxDistributionsTotalsValueAtom,
  );
  const saleProceeds = get(acquisitionInvestorsSaleProceedsTotalsValueAtom);

  return preferredDividends.map((value, i) => {
    return (
      value +
      principalRepayments[i] +
      profitDistributions[i] +
      (includeTax ? taxDistributions[i] : 0) +
      (saleProceeds[i] || 0)
    );
  });
});

export const coinvestInvestorDistributionsValuesAtom = atom((get) => {
  const includeTax = get(includeTaxDistributionsInIRRAndMOICAtom);

  const preferredDividends = get(
    searchersCoinvestPreferredDividendsTotalsValueAtom,
  );
  const principalRepayments = get(
    searchersCoinvestPrincipalRepaymentsTotalsValueAtom,
  );
  const profitDistributions = get(
    searchersCoinvestProfitDistributionsTotalsValueAtom,
  );
  const taxDistributions = get(
    searchersCoinvestTaxDistributionsTotalsValueAtom,
  );
  const saleProceeds = get(searchersCoinvestSaleProceedsTotalsValueAtom);

  return preferredDividends.map((value, i) => {
    return (
      value +
      principalRepayments[i] +
      profitDistributions[i] +
      (includeTax ? taxDistributions[i] : 0) +
      (saleProceeds[i] || 0)
    );
  });
});

export const rolloverInvestorDistributionsValuesAtom = atom((get) => {
  const includeTax = get(includeTaxDistributionsInIRRAndMOICAtom);

  const preferredDividends = get(
    sellerRolloverPreferredDividendsTotalsValueAtom,
  );
  const principalRepayments = get(
    sellerRolloverPrincipalRepaymentsTotalsValueAtom,
  );
  const profitDistributions = get(
    sellerRolloverProfitDistributionsTotalsValueAtom,
  );
  const taxDistributions = get(sellerRolloverTaxDistributionsTotalsValueAtom);
  const saleProceeds = get(sellerRolloverSaleProceedsTotalsValueAtom);

  return preferredDividends.map((value, i) => {
    return (
      value +
      principalRepayments[i] +
      profitDistributions[i] +
      (includeTax ? taxDistributions[i] : 0) +
      (saleProceeds[i] || 0)
    );
  });
});

export const totalInvestorDistributionsValuesAtom = atom((get) => {
  const searchPhase = get(searchPhaseInvestorDistributionsValuesAtom);
  const acquisition = get(acquisitionInvestorDistributionsValuesAtom);
  const coinvest = get(coinvestInvestorDistributionsValuesAtom);
  const rollover = get(rolloverInvestorDistributionsValuesAtom);

  const length = Math.max(
    searchPhase.length,
    acquisition.length,
    coinvest.length,
    rollover.length,
  );

  return Array.from(
    { length },
    (_, i) =>
      (searchPhase[i] || 0) +
      (acquisition[i] || 0) +
      (coinvest[i] || 0) +
      (rollover[i] || 0),
  );
});

export const totalInvestorDistributionsTotalAtom = atom((get) => {
  // sum totalInvestorDistributionsValuesAtom
  const values = get(totalInvestorDistributionsValuesAtom);
  return values.reduce((sum, value) => sum + value, 0);
});

export const cumulativePrincipalRepaymentValuesAtom = atom((get) => {
  const atoms = get(
    waterfallPrincipalRepaymentCumulativePrincipalRepaidMolecule,
  );

  return atoms
    .map((atomItem) => Number(atomItem ? get(atomItem).value : atomItem))
    .slice(2);
});

export const cashOnReturnAtom = atom((get) => {
  const returns = get(totalInvestorDistributionsValuesAtom);
  const totalEquity = get(totalInvestorDistributionsTotalAtom);
  if (totalEquity <= 0) return returns.map(() => 0);
  return returns.map((value) => (value <= 0 ? 0 : value / totalEquity));
});

export const cashOnReturnTotalAtom = atom((get) => {
  const returns = get(cashOnReturnAtom);
  return returns.reduce((acc, val) => acc + val, 0);
});

export const individualInvestorReturnsAtom = atom((get) => {
  const cash = get(cashOnReturnAtom);
  const startDollar =
    toNumberOrNull(get(individualInvestorReturnStartDollarAtom)) || 0;

  return cash.map((i) => {
    return i * startDollar;
  });
});

export const individualInvestorReturnsTotalAtom = atom((get) => {
  const returns = get(individualInvestorReturnsAtom);
  return returns.reduce((acc, val) => acc + val, 0);
});

// IRR
function createInvestorIrrAtom(
  capitalInputAtom: Atom<number | null | MathResult>,
  distributionsValuesAtom: Atom<number[]>,
) {
  return atom((get) => {
    const capitalInput = get(capitalInputAtom);
    const values = get(distributionsValuesAtom);

    if (!capitalInput) return null;

    try {
      return irr([-Number(capitalInput), ...values]);
    } catch (e) {
      return null;
    }
  });
}

export const searchPhaseInvestorIrrAtom = createInvestorIrrAtom(
  equitySearchPhaseCapitalDollarAtom,
  searchPhaseInvestorDistributionsValuesAtom,
);

export const acquisitionInvestorIrrAtom = createInvestorIrrAtom(
  acquisitionInvestorsCapitalInvestedAtom,
  acquisitionInvestorDistributionsValuesAtom,
);

export const coinvestInvestorIrrAtom = createInvestorIrrAtom(
  equityDollarWithCapitalAtom,
  coinvestInvestorDistributionsValuesAtom,
);

export const rolloverInvestorIrrAtom = createInvestorIrrAtom(
  sellerRolloverCapitalInvestedAtom,
  rolloverInvestorDistributionsValuesAtom,
);

// MOIC
// IRR
function createInvestorMOICAtom(
  capitalInputAtom: Atom<number | null | MathResult>,
  totalReturnAtom: Atom<number | null | MathResult>,
) {
  return atom((get) => {
    const capitalInput = get(capitalInputAtom);
    const totalReturn = get(totalReturnAtom);

    if (!capitalInput || !totalReturn) return null;

    return Number(totalReturn) / Number(capitalInput);
  });
}
export const searchPhaseInvestorMoicAtom = createInvestorMOICAtom(
  equitySearchPhaseCapitalDollarAtom,
  searchPhaseInvestorsTotalReturnAtom,
);

export const acquisitionInvestorMoicAtom = createInvestorMOICAtom(
  acquisitionInvestorsCapitalInvestedAtom,
  acquisitionInvestorsTotalReturnAtom,
);

export const coinvestInvestorMoicAtom = createInvestorMOICAtom(
  equityDollarWithCapitalAtom,
  searchersCoinvestTotalReturnAtom,
);

export const rolloverInvestorMoicAtom = createInvestorMOICAtom(
  sellerRolloverCapitalInvestedAtom,
  sellerRolloverTotalReturnAtom,
);

export type ReturnMetricsAtoms = {
  title: string;
  moic: Atom<number | MathEvaluteError | null>;
  irr: Atom<number | MathEvaluteError | null>;
  totalReturnPreferred: Atom<number | MathEvaluteError | null>;
  totalReturnCommon: Atom<number | MathEvaluteError | null>;
  totalReturnTotal: Atom<number | MathEvaluteError | null>;
  saleProceedsCommon: Atom<number | MathEvaluteError | null>;
  totalDistributionsTotal: Atom<number | MathEvaluteError | null>;
  totalDistributionsCommon: Atom<number | MathEvaluteError | null>;
  totalDistributionsPreferred: Atom<number | MathEvaluteError | null>;
  commonOwnershipPercent: Atom<number | MathEvaluteError | null>;
  preferredOwnershipPercent: Atom<number | MathEvaluteError | null>;
  isVisible?: Atom<boolean>;
};

export const basicReturnMetricsAtoms: ReturnMetricsAtoms[] = [
  {
    title: "Search Phase Investors",
    moic: searchPhaseInvestorMoicAtom,
    irr: searchPhaseInvestorIrrAtom,
    totalReturnPreferred: searchPhaseInvestorsPreferredTotalReturnAtom,
    totalReturnCommon: searchPhaseInvestorsCommonTotalReturnAtom,
    totalReturnTotal: searchPhaseInvestorsTotalReturnAtom,
    saleProceedsCommon: searchPhaseInvestorsSaleProceedsSumAtom,
    totalDistributionsTotal: searchPhaseInvestorsTotalDistributionsTotalAtom,
    totalDistributionsCommon: searchPhaseInvestorsCommonTotalDistributionsAtom,
    totalDistributionsPreferred:
      searchPhaseInvestorsPreferedTotalDistributionsAtom,
    commonOwnershipPercent: searchPhaseInvestorsPercentCommonEquityAtom,
    preferredOwnershipPercent: searchPhaseInvestorsPercentPreferredEquityAtom,
    isVisible: isInvestorFundedSearchPhaseAtom,
  },
  {
    title: "Acquisition Investors",
    moic: acquisitionInvestorMoicAtom,
    irr: acquisitionInvestorIrrAtom,
    totalReturnPreferred: acquisitionInvestorsPreferredTotalReturnAtom,
    totalReturnCommon: acquisitionInvestorsCommonTotalReturnAtom,
    totalReturnTotal: acquisitionInvestorsTotalReturnAtom,
    saleProceedsCommon: acquisitionInvestorsSaleProceedsSumAtom,
    totalDistributionsTotal: acquisitionInvestorsTotalDistributionsTotalAtom,
    totalDistributionsCommon: acquisitionInvestorsCommonTotalDistributionsAtom,
    totalDistributionsPreferred:
      acquisitionInvestorsPreferedTotalDistributionsAtom,
    commonOwnershipPercent: acquisitionInvestorsPercentCommonEquityAtom,
    preferredOwnershipPercent: acquisitionInvestorsPercentPreferredEquityAtom,
    isVisible: isEquityInvestorsIncludedAtom,
  },
  {
    title: "Searcher Co-Invest",
    moic: coinvestInvestorMoicAtom,
    irr: coinvestInvestorIrrAtom,
    totalReturnPreferred: searchersCoinvestPreferredTotalReturnAtom,
    totalReturnCommon: searchersCoinvestCommonTotalReturnAtom,
    totalReturnTotal: searchersCoinvestTotalReturnAtom,
    saleProceedsCommon: searchersCoinvestSaleProceedsSumAtom,
    totalDistributionsTotal: searchersCoinvestTotalDistributionsTotalAtom,
    totalDistributionsCommon: searchersCoinvestCommonTotalDistributionsAtom,
    totalDistributionsPreferred:
      searchersCoinvestPreferedTotalDistributionsAtom,
    commonOwnershipPercent: searcherCoInvestPercentCommonEquityAtom,
    preferredOwnershipPercent: coInvestorsPercentPreferredEquityAtom,
  },
  {
    title: "Seller Rollover",
    moic: rolloverInvestorMoicAtom,
    irr: rolloverInvestorIrrAtom,
    totalReturnPreferred: sellerRolloverPreferredTotalReturnAtom,
    totalReturnCommon: sellerRolloverCommonTotalReturnAtom,
    totalReturnTotal: sellerRolloverTotalReturnAtom,
    saleProceedsCommon: sellerRolloverSaleProceedsSumAtom,
    totalDistributionsTotal: sellerRolloverTotalDistributionsTotalAtom,
    totalDistributionsCommon: sellerRolloverCommonTotalDistributionsAtom,
    totalDistributionsPreferred: sellerRolloverPreferedTotalDistributionsAtom,
    commonOwnershipPercent: sellerRolloverPercentCommonEquityAtom,
    preferredOwnershipPercent: sellerRolloverPercentPreferredEquityAtom,
    isVisible: isSellerRollingOverEquityAtom,
  },
  {
    title: "Promote (Searcher's Carry)",
    moic: atom(null),
    irr: atom(null),
    totalReturnPreferred: promoteSearchersCarryPreferredTotalReturnAtom,
    totalReturnCommon: promoteSearchersCarryCommonTotalReturnAtom,
    totalReturnTotal: promoteSearchersCarryTotalReturnAtom,
    saleProceedsCommon: searchersCarrySaleProceedsSumAtom,
    totalDistributionsTotal: promoteSearchersCarryTotalDistributionsTotalAtom,
    totalDistributionsCommon: promoteSearchersCarryCommonTotalDistributionsAtom,
    totalDistributionsPreferred: sellerRolloverPreferedTotalDistributionsAtom,
    commonOwnershipPercent: promoteSearchersCarryPercentCommonEquityAtom,
    preferredOwnershipPercent: atom(0),
  },
] as const;

const coInPromoPreferredEquityPercentAtom = atom((get) => {
  const searcherCoInvestPercent = get(coInvestorsPercentPreferredEquityAtom);
  // carry is here, but its always 0 for preferred equity
  return searcherCoInvestPercent;
});

const coInPromoCommonEquityPercentAtom = atom((get) => {
  const searcherCoInvestPercent = get(searcherCoInvestPercentCommonEquityAtom);
  const promotePercent = get(promoteSearchersCarryPercentCommonEquityAtom);
  return Number(searcherCoInvestPercent) + Number(promotePercent);
});

const externalInvestorsPreferredEquityPercentAtom = atom((get) => {
  const searchPasePercent = get(searchPhaseInvestorsPercentPreferredEquityAtom);
  const acquisitionInvestorsPercent = get(
    acquisitionInvestorsPercentPreferredEquityAtom,
  );
  const rolloverPercent = get(sellerRolloverPercentPreferredEquityAtom);

  return (
    Number(searchPasePercent) +
    Number(acquisitionInvestorsPercent) +
    Number(rolloverPercent)
  );
});

const externalInvestorsCommonEquityPercentAtom = atom((get) => {
  const searchPhasePercent = get(searchPhaseInvestorsPercentCommonEquityAtom);
  const acquisitionInvestorsPercent = get(
    acquisitionInvestorsPercentCommonEquityAtom,
  );
  const rolloverPercent = get(sellerRolloverPercentCommonEquityAtom);

  return (
    Number(searchPhasePercent) +
    Number(acquisitionInvestorsPercent) +
    Number(rolloverPercent)
  );
});

const coInPromoPreferredTotalDistributionsAtom = atom((get) => {
  return get(searchersCoinvestPreferedTotalDistributionsAtom);
});

const coInPromoCommonTotalDistributionsAtom = atom((get) => {
  const searchersCoinvestCommonTotalDistributions = get(
    searchersCoinvestCommonTotalDistributionsAtom,
  );
  const promoteSearchersCarryCommonTotalDistributions = get(
    promoteSearchersCarryCommonTotalDistributionsAtom,
  );

  return (
    searchersCoinvestCommonTotalDistributions +
    promoteSearchersCarryCommonTotalDistributions
  );
});

export const coInPromoTotalDistributionsAtom = atom((get) => {
  return (
    get(coInPromoCommonTotalDistributionsAtom) +
    get(coInPromoPreferredTotalDistributionsAtom)
  );
});

export const externalPreferredTotalDistributionsAtom = atom((get) => {
  const searchPhasePreferred = get(
    searchPhaseInvestorsPreferedTotalDistributionsAtom,
  );
  const acquisitionPreferred = get(
    acquisitionInvestorsPreferedTotalDistributionsAtom,
  );
  const rolloverPreferred = get(sellerRolloverPreferedTotalDistributionsAtom);
  return searchPhasePreferred + acquisitionPreferred + rolloverPreferred;
});

export const externalCommonTotalDistributionsAtom = atom((get) => {
  const searchPhaseCommon = get(
    searchPhaseInvestorsCommonTotalDistributionsAtom,
  );
  const acquisitionCommon = get(
    acquisitionInvestorsCommonTotalDistributionsAtom,
  );
  const rolloverCommon = get(sellerRolloverCommonTotalDistributionsAtom);
  return searchPhaseCommon + acquisitionCommon + rolloverCommon;
});

export const externalTotalDistributionsAtom = atom((get) => {
  return (
    get(externalCommonTotalDistributionsAtom) +
    get(externalPreferredTotalDistributionsAtom)
  );
});

export const coInPromoCommonSaleProceeds = atom((get) => {
  const searchersCoinvestSaleProceeds = get(
    searchersCoinvestSaleProceedsSumAtom,
  );
  const promoteSearchersCarrySaleProceeds = get(
    searchersCarrySaleProceedsSumAtom,
  );

  return searchersCoinvestSaleProceeds + promoteSearchersCarrySaleProceeds;
});

export const externalCommSaleProceeds = atom((get) => {
  const searchPhaseSaleProceeds = get(searchPhaseInvestorsSaleProceedsSumAtom);
  const acquisitionSaleProceeds = get(acquisitionInvestorsSaleProceedsSumAtom);
  const rolloverSaleProceeds = get(sellerRolloverSaleProceedsSumAtom);
  return (
    searchPhaseSaleProceeds + acquisitionSaleProceeds + rolloverSaleProceeds
  );
});

const coInPromoPreferredTotalReturnAtom = atom((get) => {
  return get(coInPromoPreferredTotalDistributionsAtom);
});

const coInPromoCommonTotalReturnAtom = atom((get) => {
  return (
    get(coInPromoCommonTotalDistributionsAtom) +
    get(coInPromoCommonSaleProceeds)
  );
});

const coInPromoTotalReturnAtom = atom((get) => {
  return (
    get(coInPromoPreferredTotalReturnAtom) + get(coInPromoCommonTotalReturnAtom)
  );
});

const externalPreferredTotalReturnAtom = atom((get) => {
  return get(externalPreferredTotalDistributionsAtom);
});

const externalCommonTotalReturnAtom = atom((get) => {
  return (
    get(externalCommonTotalDistributionsAtom) + get(externalCommSaleProceeds)
  );
});

const externalTotalReturnAtom = atom((get) => {
  return (
    get(externalPreferredTotalReturnAtom) + get(externalCommonTotalReturnAtom)
  );
});

const promoteSearchersDistributionsValuesAtom = atom((get) => {
  const tax = get(waterfallTaxDistributionsPromoteSearchersCarryMolecule);
  const profit = get(waterfallProfitDistributionsPromoteSearchersCarryMolecule);
  const saleProceed = get(waterfallcompanySalePromoteSearchersCarryMolecule);
  const isTaxIncluded = get(includeTaxDistributionsInIRRAndMOICAtom);

  const values = tax.map((taxAtom, i) => {
    const p = get(profit[i]).value || 0;
    const s = get(saleProceed[i]).value || 0;
    const t = isTaxIncluded ? Number(get(taxAtom).value) || 0 : 0;
    return Number(t) + Number(p) + Number(s);
  });

  return values.slice(2);
});

const coInProTotalInvestorsDistributionsValuesAtom = atom((get) => {
  const coInvestValues = get(coinvestInvestorDistributionsValuesAtom);
  const promoteSearchersValues = get(promoteSearchersDistributionsValuesAtom);

  return coInvestValues.map((value, i) => {
    return Number(value) + Number(promoteSearchersValues[i]);
  });
});

const coInPromoIRRAtom = atom((get) => {
  // Calculate IRR for co-invest and promote searchers
  const capitalInput = get(equityDollarWithCapitalAtom);
  const values = get(coInProTotalInvestorsDistributionsValuesAtom);

  try {
    return irr([-Number(capitalInput), ...values]);
  } catch (e) {
    return null;
  }
});

const coInPromoMoicAtom = atom((get) => {
  return Number(get(coInPromoTotalReturnAtom)) / Number(get(equityDollarAtom));
});

const externalTotalInvestorsDistributionsValuesAtom = atom((get) => {
  const searchPhaseValues = get(searchPhaseInvestorDistributionsValuesAtom);
  const acquisitionValues = get(acquisitionInvestorDistributionsValuesAtom);
  const rolloverValues = get(rolloverInvestorDistributionsValuesAtom);

  return searchPhaseValues.map((value, i) => {
    return (
      Number(value) + Number(acquisitionValues[i]) + Number(rolloverValues[i])
    );
  });
});

const externalInvestorCapitalInvestedAtom = atom((get) => {
  // =AF120+AF84+AF48
  const AF120 = Number(get(equitySellerRolloverDollarAtom)); //Equity (Seller Rollover)
  const AF84 = Number(get(acquisitionInvestorsCapitalInvestedAtom));
  const AF48 = Number(get(equitySearchPhaseCapitalDollarAtom));
  return Number(AF120) + Number(AF84) + Number(AF48);
});

const externalIRRAtom = atom((get) => {
  const capitalInput = get(externalInvestorCapitalInvestedAtom);
  const values = get(externalTotalInvestorsDistributionsValuesAtom);

  try {
    return irr([-Number(capitalInput), ...values]);
  } catch (e) {
    return null;
  }
});

const externalMoicAtom = atom((get) => {
  return (
    get(externalTotalReturnAtom) / get(externalInvestorCapitalInvestedAtom)
  );
});

export const coReturnMetricsAtoms: ReturnMetricsAtoms[] = [
  {
    title: "Searcher (Co-Invest + Promote)",
    moic: coInPromoMoicAtom,
    irr: coInPromoIRRAtom,
    totalReturnPreferred: coInPromoPreferredTotalReturnAtom,
    totalReturnCommon: coInPromoCommonTotalReturnAtom,
    totalReturnTotal: coInPromoTotalReturnAtom,
    saleProceedsCommon: coInPromoCommonSaleProceeds,
    totalDistributionsTotal: coInPromoTotalDistributionsAtom,
    totalDistributionsCommon: coInPromoCommonTotalDistributionsAtom,
    totalDistributionsPreferred: coInPromoPreferredTotalDistributionsAtom,
    commonOwnershipPercent: coInPromoCommonEquityPercentAtom,
    preferredOwnershipPercent: coInPromoPreferredEquityPercentAtom,
  },
  {
    title: "External Investors",
    moic: externalMoicAtom,
    irr: externalIRRAtom,
    totalReturnPreferred: externalPreferredTotalReturnAtom,
    totalReturnCommon: externalCommonTotalReturnAtom,
    totalReturnTotal: externalTotalReturnAtom,
    saleProceedsCommon: externalCommSaleProceeds,
    totalDistributionsTotal: externalTotalDistributionsAtom,
    totalDistributionsCommon: externalCommonTotalDistributionsAtom,
    totalDistributionsPreferred: externalPreferredTotalDistributionsAtom,
    commonOwnershipPercent: externalInvestorsCommonEquityPercentAtom,
    preferredOwnershipPercent: externalInvestorsPreferredEquityPercentAtom,
  },
] as const;

const INVESTOR_TYPE_IDS = [
  "all",
  "searchPhase",
  "acquisition",
  "coinvest",
  "rollover",
  "promote",
] as const;

export type InvestorTypeId = (typeof INVESTOR_TYPE_IDS)[number];

export type InvestorType = {
  id: InvestorTypeId;
  title: string;
  investedCapital: number | null | MathResult;
  stepUpElection: boolean | null;
  equityType: "preferred" | "common" | null;
  stepUpApplies: boolean | null;
  multiplier: number | null | MathResult;
  isVisible: boolean | Atom<boolean>;
};

export const allInvestorTypesAtom = atom<InvestorType[]>((get) => {
  return [
    {
      id: "all",
      title: "All Investors",
      investedCapital: get(totalInvestorDistributionsTotalAtom),
      stepUpElection: null,
      equityType: null,
      stepUpApplies: null,
      multiplier: null,
      isVisible: true,
    },
    {
      id: "searchPhase",
      title: "Search Phase Investors",
      investedCapital: get(searchPhaseInvestorsCapitalInvestedAtom),
      stepUpElection: get(equityTreatmentSearchPhaseApplyStepUpAtom),
      equityType: get(equityTreatmentSearchPhaseAtom),
      stepUpApplies: get(isInvestorFundedSearchPhaseAtom),
      multiplier: get(equityInvestorEquityStepUpAtom),
      isVisible: get(isInvestorFundedSearchPhaseAtom),
    },
    {
      id: "acquisition",
      title: "Acquisition Investors",
      investedCapital: get(acquisitionInvestorsCapitalInvestedAtom),
      stepUpElection: get(equityTreatmentAcquisitionInvestorsApplyStepUpAtom),
      equityType: get(equityTreatmentAcquisitionInvestorsAtom),
      stepUpApplies: true,
      multiplier: get(equityInvestorEquityStepUpAtom),
      isVisible: get(isEquityInvestorsIncludedAtom),
    },
    {
      id: "coinvest",
      title: "Searcher Co-Invest",
      investedCapital: get(equityDollarWithCapitalAtom),
      stepUpElection: get(equityTreatmentSearchersCoInvestApplyStepUpAtom),
      equityType: get(equityTreatmentSearchersCoInvestAtom),
      stepUpApplies: true,
      multiplier: get(equityInvestorEquityStepUpAtom),
      isVisible: true,
    },
    {
      id: "rollover",
      title: "Seller Equity Rollover",
      investedCapital: get(sellerRolloverCapitalInvestedAtom),
      stepUpElection: get(equityTreatmentSellerEquityRolloverApplyStepUpAtom),
      equityType: get(equityTreatmentSellerEquityRolloverAtom),
      stepUpApplies: get(isSellerRollingOverEquityAtom),
      multiplier: get(equityInvestorEquityStepUpAtom),
      isVisible: get(isSellerRollingOverEquityAtom),
    },
    {
      id: "promote",
      title: "Promote (Searcher's Carry)",
      investedCapital: null,
      stepUpElection: null,
      equityType: "common",
      stepUpApplies: null,
      multiplier: null,
      isVisible: get(promoteSearchersCarryVisibleAtom),
    },
  ] as const;
});

function getMoleculeValues(get: Getter, molecule: Atom<Atom<CellTypes>[]>) {
  return get(molecule)
    .map((a) => Number(get(a).value))
    .slice(2);
}

function getArrayTotal(arr: number[]) {
  return arr.reduce((a, b) => a + b, 0);
}

function createInvestorDistributionTable(
  get: Getter,
  title: string,
  id: InvestorTypeId,
  preferredDividendsMolecule: Atom<Atom<CellTypes>[]>,
  principalRepaymentsMolecule: Atom<Atom<CellTypes>[]>,
  profitDistributionsMolecule: Atom<Atom<CellTypes>[]>,
  taxDistributionsMolecule: Atom<Atom<CellTypes>[]>,
  saleProceedsMolecule: Atom<Atom<CellTypes>[]>,
  totalInvestorDistributionsAtom: Atom<number[]>,
) {
  const preferredDividends = getMoleculeValues(get, preferredDividendsMolecule);
  const principalRepayments = getMoleculeValues(
    get,
    principalRepaymentsMolecule,
  );
  const profitDistributions = getMoleculeValues(
    get,
    profitDistributionsMolecule,
  );
  const taxDistributions = getMoleculeValues(get, taxDistributionsMolecule);
  const proceedsFromCompanySale = getMoleculeValues(get, saleProceedsMolecule);
  const totalInvestorDistributions = get(totalInvestorDistributionsAtom);

  const periods = preferredDividends.map((_, i) => i + 1);
  const years = preferredDividends.map(
    (_, i) => i + get(sbaLoanStartDateAtom).toDate().getFullYear(),
  );

  return {
    id,
    title,
    periods,
    preferredDividends,
    years,
    principalRepayments,
    profitDistributions,
    taxDistributions,
    proceedsFromCompanySale,
    totalInvestorDistributions,
    totals: {
      preferredDividends: getArrayTotal(preferredDividends),
      principalRepayments: getArrayTotal(principalRepayments),
      profitDistributions: getArrayTotal(profitDistributions),
      taxDistributions: getArrayTotal(taxDistributions),
      proceedsFromCompanySale: getArrayTotal(proceedsFromCompanySale),
      totalInvestorDistributions: getArrayTotal(totalInvestorDistributions),
    } as const,
  } as const;
}

export type InvestorDistributionTable = ReturnType<
  typeof createInvestorDistributionTable
>;

export const searchPhaseInvestorsDistributionTableAtom = atom((get) =>
  createInvestorDistributionTable(
    get,
    "Search Phase Investors",
    "searchPhase",
    waterfallPreferredDividendsSearchPhaseInvestorsMolecule,
    waterfallPrincipalRepaymentSearchPhaseInvestorsMolecule,
    waterfallProfitDistributionsSearchPhaseInvestorsMolecule,
    searchPhaseInvestorsROITaxesMolecule,
    waterfallcompanySaleSearchPhaseInvestorsMolecule,
    searchPhaseInvestorDistributionsValuesAtom,
  ),
);

export const acquisitionInvestorsDistributionTableAtom = atom((get) =>
  createInvestorDistributionTable(
    get,
    "Acquisition Investors",
    "acquisition",
    waterfallPreferredDividendsAquisitionInvestorsMolecule,
    waterfallPrincipalRepaymentAquisitionInvestorsMolecule,
    waterfallProfitDistributionsAcquisitionInvestorsMolecule,
    acquisitionInvestorsROITaxesMolecule,
    waterfallcompanySaleAcquisitionInvestorsMolecule,
    acquisitionInvestorDistributionsValuesAtom,
  ),
);

export const searchersCoinvestDistributionTableAtom = atom((get) =>
  createInvestorDistributionTable(
    get,
    "Searcher Co-Invest",
    "coinvest",
    waterfallPreferredDividendsSearchersCoinvestMolecule,
    waterfallPrincipalRepaymentSearchersCoinvestMolecule,
    waterfallProfitDistributionsSearchersCoinvestMolecule,
    searchersCoinvestROITaxesMolecule,
    waterfallcompanySaleSearchersCoinvestMolecule,
    coinvestInvestorDistributionsValuesAtom,
  ),
);

export const sellerRolloverDistributionTableAtom = atom((get) =>
  createInvestorDistributionTable(
    get,
    "Seller Equity Rollover",
    "rollover",
    waterfallPreferredDividendsSellerRolloverMolecule,
    waterfallPrincipalRepaymentSellerRolloverMolecule,
    waterfallProfitDistributionsSellerRolloverMolecule,
    sellerRolloverROITaxesMolecule,
    waterfallcompanySaleSellerRolloverMolecule,
    rolloverInvestorDistributionsValuesAtom,
  ),
);

export const searcherPromoteDistributionTableAtom = atom((get) => {
  const taxDistributions = getMoleculeValues(
    get,
    promoteSearchersCarryROITaxesMolecule,
  );
  const profitDistributions = getMoleculeValues(
    get,
    waterfallProfitDistributionsPromoteSearchersCarryMolecule,
  );
  const proceedsFromCompanySale = getMoleculeValues(
    get,
    waterfallcompanySalePromoteSearchersCarryMolecule,
  );
  const totalInvestorDistributions = get(
    promoteSearchersDistributionsValuesAtom,
  );
  const periods = taxDistributions.map((_, i) => i + 1);
  const years = taxDistributions.map(
    (_, i) => i + get(sbaLoanStartDateAtom).toDate().getFullYear(),
  );

  return {
    title: "Searcher Promote",
    id: "promote",
    periods,
    years,
    preferredDividends: taxDistributions.map(() => 0),
    principalRepayments: taxDistributions.map(() => 0),
    profitDistributions,
    taxDistributions,
    proceedsFromCompanySale,
    totalInvestorDistributions,
    totals: {
      preferredDividends: 0,
      principalRepayments: 0,
      profitDistributions: getArrayTotal(profitDistributions),
      taxDistributions: getArrayTotal(taxDistributions),
      proceedsFromCompanySale: getArrayTotal(proceedsFromCompanySale),
      totalInvestorDistributions: getArrayTotal(totalInvestorDistributions),
    },
  } as InvestorDistributionTable;
});

// Take two tables and return sum each property
// assume same length
const mergeTables = (
  newTitle: string,
  id: InvestorTypeId,
  ...tables: InvestorDistributionTable[]
): InvestorDistributionTable => {
  if (tables.length === 0) {
    throw new Error("At least one table must be provided");
  }

  const periods = tables[0].periods;
  const years = tables[0].years;

  // Helper to sum arrays element-wise for any number of arrays
  function sumArrays(arrays: number[][]): number[] {
    const length = Math.max(...arrays.map((arr) => arr.length));
    return Array.from({ length }, (_, i) =>
      arrays.reduce((sum, arr) => sum + (arr[i] || 0), 0),
    );
  }

  const preferredDividends = sumArrays(tables.map((t) => t.preferredDividends));
  const principalRepayments = sumArrays(
    tables.map((t) => t.principalRepayments),
  );
  const profitDistributions = sumArrays(
    tables.map((t) => t.profitDistributions),
  );
  const taxDistributions = sumArrays(tables.map((t) => t.taxDistributions));
  const proceedsFromCompanySale = sumArrays(
    tables.map((t) => t.proceedsFromCompanySale),
  );
  const totalInvestorDistributions = sumArrays(
    tables.map((t) => t.totalInvestorDistributions),
  );

  const totals = {
    preferredDividends: getArrayTotal(preferredDividends),
    principalRepayments: getArrayTotal(principalRepayments),
    profitDistributions: getArrayTotal(profitDistributions),
    taxDistributions: getArrayTotal(taxDistributions),
    proceedsFromCompanySale: getArrayTotal(proceedsFromCompanySale),
    totalInvestorDistributions: getArrayTotal(totalInvestorDistributions),
  };

  return {
    id,
    title: newTitle,
    totals,
    periods,
    years,
    preferredDividends,
    principalRepayments,
    profitDistributions,
    taxDistributions,
    proceedsFromCompanySale,
    totalInvestorDistributions,
  };
};

export const allDistributionTableAtom = atom((get) => {
  return mergeTables(
    "All Investors",
    "all",
    get(searchPhaseInvestorsDistributionTableAtom),
    get(acquisitionInvestorsDistributionTableAtom),
    get(searchersCoinvestDistributionTableAtom),
    get(sellerRolloverDistributionTableAtom),
    get(searcherPromoteDistributionTableAtom),
  );
});

export const investorDistributionTableListAtom = atom((get) => {
  return [
    get(allDistributionTableAtom),
    get(searchPhaseInvestorsDistributionTableAtom),
    get(acquisitionInvestorsDistributionTableAtom),
    get(searchersCoinvestDistributionTableAtom),
    get(sellerRolloverDistributionTableAtom),
    get(searcherPromoteDistributionTableAtom),
  ] as const;
});

export const cashOnCashTableListAtom = atom((get) => {
  return get(investorDistributionTableListAtom).map((table) => {
    let investedCapital = Number(
      get(allInvestorTypesAtom).find((type) => type.id === table.id)
        ?.investedCapital,
    );

    if (table.id === "all") {
      investedCapital = Number(get(totalCapitalInvestedAtom));
    }

    const cashOnCash = table.totalInvestorDistributions.map((value) => {
      if (!investedCapital || investedCapital === 0) return 0;
      return value / investedCapital;
    });

    const totalCashOnCash = cashOnCash.reduce((a, b) => a + b, 0);

    return {
      id: table.id,
      title: table.title,
      cashOnCash,
      totalCashOnCash,
      investedCapital,
    };
  });
});

export const individualInvestorReturnsTableListAtom = atom((get) => {
  const startDollar =
    toNumberOrNull(get(individualInvestorReturnStartDollarAtom)) || 0;

  return get(cashOnCashTableListAtom).map((table) => {
    const individualReturns = table.cashOnCash.map((i) => i * startDollar);
    const totalIndividualReturns = individualReturns.reduce((a, b) => a + b, 0);

    return {
      id: table.id,
      title: table.title,
      individualReturns,
      totalIndividualReturns,
    };
  });
});

export const cumulativePrincipalRepaidTableListAtom = atom((get) => {
  const cumulativePrincipalRepaidItems = get(
    cumulativePrincipalRepaymentValuesAtom,
  );

  return get(investorDistributionTableListAtom).map((table) => {
    let cumulativeValue = 0;

    const cumulativePrincipalRepaid = cumulativePrincipalRepaidItems.map(
      (value, i) => {
        get(investorDistributionTableListAtom);

        const cumulativePrincipalRepaid = value;

        if (table.id === "all") {
          return cumulativePrincipalRepaid;
        }

        cumulativeValue += table.principalRepayments[i] || 0;

        return cumulativeValue;
      },
    ) as (number | null)[];

    // if the last items off the are are the same, make all but the first occurrence null
    // for example [1,2,3,4,5,5,5] becomes [1,2,3,4,5,null,null]
    let foundItemsCount = 0;
    const lastValue =
      cumulativePrincipalRepaid[cumulativePrincipalRepaid.length - 1];
    for (let i = 0; i < cumulativePrincipalRepaid.length - 1; i++) {
      if (cumulativePrincipalRepaid[i] === lastValue) {
        foundItemsCount++;
      }
    }

    if (
      foundItemsCount > 0 &&
      foundItemsCount !== cumulativePrincipalRepaid.length - 1
    ) {
      for (let i = 0; i < foundItemsCount; i++) {
        cumulativePrincipalRepaid[cumulativePrincipalRepaid.length - 1 - i] =
          null;
      }
    }

    return {
      id: table.id,
      cumulativePrincipalRepaid,
    };
  });
});
